import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate } from '../../utils/formatters.js';

import { ReserveStockModal } from './ReserveStockModal.jsx';

export function ReservationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const list = useQuery({
    queryKey: ['inventory', 'reservations'],
    queryFn: () => inventoryApi.listReservations().then((r) => r.items),
  });
  const rows = list.data || [];
  const openRows = rows.filter((r) => r.status === 'OPEN');
  const totalReserved = openRows.reduce((a, r) => a + Number(r.quantity || 0), 0);

  const release = useMutation({
    mutationFn: (id) => inventoryApi.releaseReservation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory', 'reservations'] }),
  });

  const columns = [
    { key: 'createdAt', label: t('res.col.date'), render: (r) => formatDate(r.createdAt) },
    { key: 'product', label: t('res.col.product'), render: (r) => `${r.sku} · ${r.productName}` },
    { key: 'warehouse', label: t('res.col.warehouse'), render: (r) => r.warehouseCode || '-' },
    { key: 'quantity', label: t('res.col.quantity'), align: 'right', render: (r) => r.quantity },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={r.status === 'OPEN' ? 'warning' : 'neutral'}>{t(`res.status.${r.status}`)}</Badge> },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (r) => (r.status === 'OPEN'
        ? <Button size="sm" variant="ghost" disabled={release.isPending} onClick={() => release.mutate(r.id)}>{t('res.action.release')}</Button>
        : null),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('res.title')}</h1>
          <p className="muted">{t('res.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setOpen(true)}>{t('res.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('res.metric.open')} value={String(openRows.length)} trendVariant={openRows.length > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('res.metric.reserved')} value={String(totalReserved)} />
        <MetricCard label={t('res.metric.total')} value={String(rows.length)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('res.empty')} />

      <ReserveStockModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
